import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";

import { apiClient, ApiError } from "../lib/api";
import { getSession } from "../lib/auth";

/**
 * Accept-invite landing screen.
 *
 * Flow:
 *   1. Read the token from the URL.
 *   2. Probe `/api/me`. If unauthed, send the user to /login with a return
 *      path back to /invitations/:token so we can resume after sign-in.
 *   3. If authed, POST /api/team/invitations/:token/accept and route to /.
 *
 * Errors (404 / 410 / 403 / 409) are surfaced inline — the user gets a
 * clear message and a sign-out link in case they signed in with the wrong
 * email.
 */
export function Invitation() {
  const { token = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const { data: session, isLoading: sessionLoading } = useQuery({
    queryKey: ["session"],
    queryFn: getSession,
    retry: false,
    staleTime: 60_000,
  });

  const accept = useMutation({
    mutationFn: () => apiClient.team.accept(token),
    onSuccess: async () => {
      // Membership changed; invalidate session-scoped caches and head home.
      // The next `/api/me` round-trip will resolve a non-null
      // currentProviderId from the new team_member row.
      await queryClient.invalidateQueries({ queryKey: ["session"] });
      navigate("/", { replace: true });
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : "Failed to accept");
    },
  });

  // Once we know the auth state, decide what to do.
  useEffect(() => {
    if (sessionLoading) return;
    if (!session?.user) {
      // Not signed in — bounce to /login. Stash the invitation URL in
      // history state via a query param so the post-login redirect lands
      // here. Better Auth's `callbackURL` is captured by `Login.tsx`; for
      // simplicity we just pass through location.
      const returnPath = `/invitations/${encodeURIComponent(token)}`;
      navigate(`/login?next=${encodeURIComponent(returnPath)}`, {
        replace: true,
      });
      return;
    }
    // Authed — fire the accept call exactly once.
    if (accept.isIdle) accept.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionLoading, session?.user?.id]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 420,
          padding: 32,
          background: "var(--ad-bg, #fff)",
          border: "1px solid var(--ad-border, #e5e7eb)",
          borderRadius: 12,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <h1 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>
          Accept invitation
        </h1>
        {sessionLoading || accept.isPending ? (
          <p
            style={{
              fontSize: 13,
              color: "var(--ad-muted, #6b7280)",
            }}
          >
            Confirming your invitation…
          </p>
        ) : error ? (
          <div
            style={{
              padding: 12,
              borderRadius: 8,
              fontSize: 13,
              background: "rgba(220, 38, 38, 0.08)",
              color: "#b91c1c",
              marginBottom: 12,
            }}
          >
            {error}
          </div>
        ) : (
          <p
            style={{
              fontSize: 13,
              color: "var(--ad-muted, #6b7280)",
            }}
          >
            Redirecting…
          </p>
        )}
      </div>
    </div>
  );
}
